import React, { lazy } from 'react'
import { useParams, useQueryParams } from '@fs/zion-router'

import * as permissions from 'config/permissions'

const SearchResultsPage = lazy(() => import('views/SearchResultsPage/SearchResultsPage'))
const VolumeSetSearchResultsPage = lazy(
  () => import('views/SearchResultsPage/VolumeSetSearch/VolumeSetSearchResultsPage/VolumeSetSearchResultsPage')
)

export default function useRoutes() {
  const routes = {
    // out of alphabetical order to prioritize the /audit/feedback route over the /audit/:id route
    cameraOperatorFeedbackDashboard: {
      auth: true,
      Component: lazy(() => import('views/CameraOperatorFeedbackDashboard/CameraOperatorFeedbackDashboard')),
      developmentOnly: false,
      path: ['/audit/feedback/:projectId', '/home/audit/feedback/:projectId'],
      permissions: permissions.cameraOperatorFeedback,
    },
    cameraOperatorFeedbackProjects: {
      auth: true,
      Component: lazy(() => import('views/CameraOperatorFeedbackDashboard/CameraOperatorFeedbackProjects')),
      developmentOnly: false,
      path: ['/audit/feedback', '/home/audit/feedback'],
      permissions: permissions.cameraOperatorFeedback,
    },
    cameraOperatorFeedbackOperator: {
      auth: true,
      Component: lazy(() => import('views/CameraOperatorFeedbackOperatorView/CameraOperatorFeedbackOperatorView')),
      developmentOnly: false,
      path: ['/audit/feedback/:projectId/project', '/home/audit/feedback/:projectId/project'],
      permissions: permissions.cameraOperatorFeedback,
    },
    auditFolders: {
      auth: true,
      Component: lazy(() => import('views/AuditFolders/AuditFolders')),
      developmentOnly: false,
      path: ['/audit/:id', '/home/audit/:id'],
      permissions: permissions.audit,
    },
    auditProjects: {
      auth: true,
      Component: lazy(() => import('views/AuditProjects/AuditProjects')),
      developmentOnly: false,
      path: ['/audit', '/home/audit'],
      permissions: permissions.audit,
    },
    cameraDashboardPage: {
      auth: true,
      Component: lazy(() => import('views/CameraDashboardPage/CameraDashboardPage')),
      developmentOnly: false,
      path: ['/camera', '/home/camera'],
      permissions: permissions.authorized,
    },
    cameraProjectDetailsPage: {
      auth: true,
      Component: lazy(() => import('views/CameraProjectDetailsPage/CameraProjectDetailsPage')),
      developmentOnly: false,
      path: ['/camera/details/:id', '/home/camera/details/:id'],
      permissions: permissions.authorized,
    },
    collectionsSearch: {
      auth: true,
      Component: lazy(() => import('views/CollectionsSearch/SearchSteps/CollectionsSearch')),
      developmentOnly: false,
      path: ['/collections-search', '/collections/search', '/images/collections-search', '/images/collections/search'],
      permissions: permissions.authorized,
    },
    collectionSearchResults: {
      auth: true,
      Component: lazy(() => import('views/CollectionsSearch/CollectionsResults/CollectionsResults')),
      developmentOnly: false,
      path: [
        '/collections-results',
        '/collections/results',
        '/images/collections-results',
        '/images/collections/results',
      ],
      permissions: permissions.authorized,
    },
    historyPage: {
      auth: true,
      Component: lazy(() => import('views/HistoryPage/HistoryPage')),
      developmentOnly: false,
      path: '/images/history',
      permissions: permissions.internalSearch,
    },
    historyPageSearchable: {
      auth: true,
      Component: lazy(() => import('views/HistoryPage/HistoryPage')),
      developmentOnly: false,
      path: '/images/history/:id',
      permissions: permissions.internalSearch,
    },
    imageOrganizeProjectsDashboard: {
      auth: true,
      Component: lazy(() => import('views/ImageOrganizeProjectsDashboard/ImageOrganizeProjectsDashboard')),
      developmentOnly: false,
      path: '/images/projects-dashboard',
      permissions: permissions.lightyearAdmin,
    },
    imageViewer: {
      auth: false,
      Component: () => {
        const { groupId } = useParams() // eslint-disable-line react-hooks/rules-of-hooks
        window.location.replace(`http://localhost:5001/ark:/61903/${groupId}?view=explore`)
        return null
      },
      developmentOnly: true,
      path: '/records/r2/ark:/61903/:groupId',
      permissions: permissions.authorized,
    },
    landingPage: {
      auth: true,
      Component: lazy(() => import('views/LandingPage/LandingPage')),
      developmentOnly: false,
      path: ['/images', '/beta'],
      permissions: permissions.authorized,
    },
    cameraOperatorManagerHome: {
      auth: true,
      Component: lazy(() => import('views/CameraOperatorManagerHome/CameraOperatorManagerHome')),
      developmentOnly: false,
      path: ['/operator/manager', '/home/operator/manager'],
      permissions: permissions.cameraOperatorManager,
    },
    cameraOperatorFeedbackDashboardWeekPage: {
      auth: true,
      Component: lazy(
        () => import('views/CameraOperatorProjectsDashboard/ProjectDashboardWeekPage/ProjectDashboardWeekPage')
      ),
      path: ['/operator/:weekEnding', '/home/operator/:weekEnding'],
      permissions: permissions.cameraOperator,
    },
    cameraOperatorDashboard: {
      auth: true,
      Component: lazy(
        () =>
          import(
            'views/CameraOperatorProjectsDashboard/CameraOperatorProjectsDashboard/CameraOperatorProjectsDashboard'
          )
      ),
      path: ['/operator', '/home/operator'],
      permissions: permissions.cameraOperator,
    },
    cameraOperator: {
      auth: true,
      Component: lazy(() => import('views/CameraOperatorManagementDashboard/CameraOperator/CameraOperator')),
      developmentOnly: false,
      path: ['/operators/:id', '/home/operators/:id'],
      permissions: permissions.cameraOperatorManagement,
    },
    cameraOperatorManagementDashboard: {
      auth: true,
      Component: lazy(() => import('views/CameraOperatorManagementDashboard/CameraOperatorManagementDashboard')),
      developmentOnly: false,
      path: ['/operators', '/home/operators'],
      permissions: permissions.cameraOperatorManagement,
    },
    projectSupervisorDashboard: {
      auth: true,
      Component: lazy(() => import('views/ProjectSupervisorDashboard/ProjectSupervisorDashboard')),
      developmentOnly: false,
      path: ['/projects/supervisor', '/home/projects/supervisor'],
      permissions: permissions.contractorSupervisor,
    },
    recordMetadataReviewPage: {
      auth: true,
      Component: lazy(() => import('views/RecordMetadataReviewPage/RecordMetadataReviewPage')),
      developmentOnly: false,
      path: ['/review', '/home/review'],
      permissions: permissions.recordMetadataReview,
    },
    recordsHomePage: {
      auth: true,
      Component: lazy(() => import('views/RecordsHomePage/RecordsHomePage')),
      developmentOnly: false,
      path: ['/home'],
      permissions: permissions.authorized,
    },
    searchResultsPage: {
      auth: true,
      Component: () => {
        const { query } = useQueryParams() // eslint-disable-line react-hooks/rules-of-hooks
        return query.volumeSetSearch ? <VolumeSetSearchResultsPage /> : <SearchResultsPage />
      },
      developmentOnly: false,
      path: ['/images/search-results', '/beta/search-results'],
      permissions: permissions.authorized,
    },
    searchResultsPageMCS: {
      auth: false,
      Component: lazy(() => import('views/SearchResultsPage/SearchResultsPage')),
      developmentOnly: false,
      path: '/images/search-results-mcs',
      permissions: permissions.authorized,
    },
    shippingAddressDashboardPage: {
      auth: true,
      Component: lazy(() => import('views/ShippingAddressDashboardPage/ShippingAddressDashboardPage')),
      developmentOnly: false,
      path: ['/address', '/home/address'],
      permissions: permissions.shippingAddress,
    },
    testPage: {
      auth: false,
      Component: lazy(() => import('views/TestPage/TestPage')),
      developmentOnly: true,
      path: '/test',
      permissions: permissions.authorized,
    },
    volumeSetImageOrganizeOrganize: {
      auth: true,
      Component: lazy(() => import('views/VolumeSetImageOrganize/organize/OrganizePage')),
      developmentOnly: false,
      path: '/images/organize/:volumeSetType(jiapu|jokbo|keizu)/:groupId',
      permissions: permissions.volumeSetImageOrganize,
    },
    volumeSetImageOrganizeSearch: {
      auth: true,
      Component: lazy(() => import('views/VolumeSetImageOrganize/search/SearchPage')),
      developmentOnly: false,
      path: '/images/organize/:volumeSetType(jiapu|jokbo|keizu)',
      permissions: permissions.volumeSetImageOrganize,
    },
    imageViewerRedirect: {
      auth: true,
      Component: lazy(() => import('views/ImageViewerRedirect/ImageViewerRedirect')),
      developmentOnly: false,
      path: '/images/redirect/:groupId',
      permissions: permissions.authorized,
    },
    contractInvoiceReview: {
      auth: true,
      Component: lazy(() => import('views/ContractInvoiceReview/ContractInvoiceReview')),
      developmentOnly: false,
      path: '/home/finance/review',
      permissions: permissions.contractInvoiceReview,
    },
    invoiceHistory: {
      auth: true,
      Component: lazy(() => import('views/ContractorInvoiceHistory/InvoiceHistory')),
      developmentOnly: false,
      path: '/projects/supervisor/invoice-history',
      permissions: permissions.contractorSupervisor,
    },
    watermarking: {
      auth: true,
      Component: lazy(() => import('views/Watermarking/components/dashboard/Dashboard')),
      developmentOnly: false,
      path: '/home/watermarking',
      permissions: permissions.watermarking,
    },
    watermarkingDetails: {
      auth: true,
      Component: lazy(() => import('views/Watermarking/components/dashboard/ViewWatermark')),
      developmentOnly: false,
      path: '/home/watermarking/:watermark_id',
      permissions: permissions.watermarking,
    },
    rfpDashboard: {
      auth: true,
      Component: lazy(() => import('views/RFP/components/generation/dashboard/RFPDashboardPage')),
      developmentOnly: false,
      path: '/home/rfp',
      permissions: permissions.rfp,
    },
    rfpVendorProfiling: {
      auth: true,
      Component: lazy(() => import('views/RFP/components/vendorProfiling/dashboard/VendorDashboard')),
      developmentOnly: false,
      path: '/home/rfp/vendor-profile',
      permissions: permissions.rfp,
    },
    rfpVendorProfileInfo: {
      auth: true,
      Component: lazy(() => import('views/RFP/components/vendorProfiling/dashboard/ProfileInformation')),
      developmentOnly: false,
      path: '/home/rfp/vendor-profile/:vendorId',
      permissions: permissions.rfp,
    },
    rfpVendor: {
      auth: true,
      Component: lazy(() => import('views/RFP/components/scoringAwarding/VendorPage')),
      developmentOnly: false,
      path: '/home/rfp/vendor',
      permissions: permissions.rfp,
    },
    rfpProjectDetail: {
      auth: true,
      Component: lazy(() => import('views/RFP/components/scoringAwarding/projectDetail/ProjectDetail')),
      developmentOnly: false,
      path: '/home/rfp/project-detail',
      permissions: permissions.rfp,
    },
  }

  return {
    chinaOnlyRoutes: [
      routes.imageViewer,
      { ...routes.landingPage, auth: false },
      { ...routes.searchResultsPage, auth: false },
      { ...routes.searchResultsPageMCS, auth: false },
      routes.testPage,
    ],
    routes: Object.values(routes),
  }
}
